import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { getTotalWorkedHours } from './attendanceService'
import Papa from 'papaparse'

/**
 * Get all timesheet entries for a specific employee
 * @param {string} employeeId - Employee ID
 * @param {number} limit - Max number of entries (optional)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getTimesheetsByEmployee = async (employeeId, limit = 100) => {
  try {
    const timesheetsQuery = query(
      collection(db, 'timesheetEntries'),
      where('employeeId', '==', employeeId),
      orderBy('date', 'desc')
    )

    const querySnapshot = await getDocs(timesheetsQuery)
    const timesheets = []

    querySnapshot.forEach((doc) => {
      timesheets.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: timesheets }
  } catch (error) {
    console.error('Get timesheets by employee error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all pending timesheet entries (admin only)
 * @param {object} userData - Current user data (for filtering by manager)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getPendingTimesheets = async (userData) => {
  try {
    let timesheetsQuery

    // Super admin sees ALL pending timesheets
    if (userData.role === 'super_admin') {
      timesheetsQuery = query(
        collection(db, 'timesheetEntries'),
        where('status', '==', 'pending'),
        orderBy('date', 'desc')
      )
    } else {
      // Non-super_admin sees only their direct reports' pending timesheets
      timesheetsQuery = query(
        collection(db, 'timesheetEntries'),
        where('status', '==', 'pending'),
        where('reportsTo', '==', userData.employeeId),
        orderBy('date', 'desc')
      )
    }

    const querySnapshot = await getDocs(timesheetsQuery)
    const timesheets = []

    querySnapshot.forEach((doc) => {
      timesheets.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: timesheets }
  } catch (error) {
    console.error('Get pending timesheets error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all timesheet entries (admin only)
 * @param {object} userData - Current user data (for filtering by manager)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllTimesheets = async (userData) => {
  try {
    let timesheetsQuery

    // Super admin sees ALL timesheets
    if (userData.role === 'super_admin') {
      timesheetsQuery = query(
        collection(db, 'timesheetEntries'),
        orderBy('date', 'desc')
      )
    } else {
      // Non-super_admin sees only their direct reports' timesheets
      timesheetsQuery = query(
        collection(db, 'timesheetEntries'),
        where('reportsTo', '==', userData.employeeId),
        orderBy('date', 'desc')
      )
    }

    const querySnapshot = await getDocs(timesheetsQuery)
    const timesheets = []

    querySnapshot.forEach((doc) => {
      timesheets.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: timesheets }
  } catch (error) {
    console.error('Get all timesheets error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get timesheet entries for a specific date
 * @param {string} employeeId - Employee ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getTimesheetsByDate = async (employeeId, date) => {
  try {
    const timesheetsQuery = query(
      collection(db, 'timesheetEntries'),
      where('employeeId', '==', employeeId),
      where('date', '==', date)
    )

    const querySnapshot = await getDocs(timesheetsQuery)
    const timesheets = []

    querySnapshot.forEach((doc) => {
      timesheets.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: timesheets }
  } catch (error) {
    console.error('Get timesheets by date error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all active projects for dropdown
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllProjects = async () => {
  try {
    const projectsSnapshot = await getDocs(collection(db, 'projects'))

    if (projectsSnapshot.empty) {
      return { success: true, data: [] }
    }

    const projects = []

    projectsSnapshot.forEach((doc) => {
      const rawData = doc.data()
      projects.push({
        id: doc.id,
        ...rawData
      })
    })

    projects.sort((a, b) => {
      if (!a.name || !b.name) return 0
      return a.name.localeCompare(b.name)
    })

    return { success: true, data: projects }
  } catch (error) {
    console.error('❌❌❌ getAllProjects CRITICAL ERROR ❌❌❌')
    console.error('❌ Error:', error)
    console.error('❌ Message:', error.message)
    console.error('❌ Code:', error.code)
    console.error('❌ Stack:', error.stack)
    console.error('═══════════════════════════════════════════════')
    return { success: false, error: error.message }
  }
}


/**
 * Get total allocated hours for a specific employee on a specific date
 * Sum of pending and approved timesheet hours
 * @param {string} employeeId - Employee ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<{ success: boolean, hours?: number, error?: string }>}
 */
export const getTotalAllocatedHours = async (employeeId, date) => {
  try {
    const timesheetsQuery = query(
      collection(db, 'timesheetEntries'),
      where('employeeId', '==', employeeId),
      where('date', '==', date),
      where('status', 'in', ['pending', 'approved'])
    )

    const querySnapshot = await getDocs(timesheetsQuery)
    let totalHours = 0

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      totalHours += data.hours || 0
    })

    return { success: true, hours: totalHours }
  } catch (error) {
    console.error('Get total allocated hours error:', error)
    return { success: false, error: error.message, hours: 0 }
  }
}

/**
 * Validate timesheet entry data
 * @param {object} data - Entry data
 * @param {string} employeeId - Employee ID
 * @param {string} excludeEntryId - Entry ID to exclude (for edit mode)
 * @returns {Promise<{ valid: boolean, error?: string }>}
 */
export const validateTimesheetEntry = async (data, employeeId, excludeEntryId = null) => {
  // Validate required fields
  if (!data.projectId || !data.date || !data.hours || !data.taskDescription) {
    return { valid: false, error: 'All fields are required' }
  }

  // Validate hours
  if (isNaN(data.hours) || data.hours <= 0) {
    return { valid: false, error: 'Hours must be greater than 0' }
  }

  // Validate task description length
  if (data.taskDescription.length > 500) {
    return { valid: false, error: 'Task description exceeds maximum length of 500 characters' }
  }

  // Validate date format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  if (!dateRegex.test(data.date)) {
    return { valid: false, error: 'Invalid date format. Use YYYY-MM-DD' }
  }

  // Validate date is not in future
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const entryDate = new Date(data.date + 'T00:00:00')

  if (entryDate > today) {
    return { valid: false, error: 'Cannot create timesheet for future dates' }
  }

  // Validate date is not more than 30 days old
  const thirtyDaysAgo = new Date(today)
  thirtyDaysAgo.setDate(today.getDate() - 30)

  if (entryDate < thirtyDaysAgo) {
    return { valid: false, error: 'Cannot create timesheet for dates older than 30 days' }
  }

  // Get attendance hours for this date
  const attendanceResult = await getTotalWorkedHours(employeeId, data.date)

  if (!attendanceResult.success) {
    return { valid: false, error: 'Unable to verify attendance hours' }
  }

  if (!attendanceResult.hasCheckOut) {
    return { valid: false, error: 'No completed attendance record for this date. Please check in and check out first.' }
  }

  const workedHours = attendanceResult.hours

  // Get existing allocated hours (excluding current entry if editing)
  const timesheetsQuery = query(
    collection(db, 'timesheetEntries'),
    where('employeeId', '==', employeeId),
    where('date', '==', data.date),
    where('status', 'in', ['pending', 'approved'])
  )

  const querySnapshot = await getDocs(timesheetsQuery)
  let existingHours = 0

  querySnapshot.forEach((docSnap) => {
    // Exclude current entry if editing
    if (excludeEntryId && docSnap.id === excludeEntryId) {
      return
    }
    const entryData = docSnap.data()
    existingHours += entryData.hours || 0
  })

  // Check if total hours would exceed worked hours
  const totalHours = existingHours + data.hours

  if (totalHours > workedHours) {
    const available = workedHours - existingHours
    return {
      valid: false,
      error: `Cannot allocate ${data.hours}h. Only ${available.toFixed(2)}h available (worked: ${workedHours.toFixed(2)}h, allocated: ${existingHours.toFixed(2)}h)`
    }
  }

  return { valid: true }
}

/**
 * Create new timesheet entry
 * @param {object} data - Entry data
 * @param {object} userData - User data
 * @returns {Promise<{ success: boolean, docId?: string, data?: object, error?: string }>}
 */
export const createTimesheet = async (data, userData) => {
  try {
    // Validate entry
    const validation = await validateTimesheetEntry(data, userData.employeeId)

    if (!validation.valid) {
      return { success: false, error: validation.error }
    }

    // Get project name
    const projectDoc = await getDoc(doc(db, 'projects', data.projectId))
    const projectName = projectDoc.exists() ? projectDoc.data().name : 'Unknown Project'

    const now = Timestamp.now()
    const entryData = {
      employeeId: userData.employeeId,
      employeeUid: userData.uid,
      employeeName: userData.fullName || userData.email,
      projectId: data.projectId,
      projectName: projectName,
      date: data.date,
      hours: parseFloat(data.hours),
      taskDescription: data.taskDescription.trim(),
      status: 'pending',
      reportsTo: userData.reportsTo || null,
      reportsToName: userData.reportsToName || null,
      reviewedBy: null,
      reviewedByName: null,
      reviewedAt: null,
      reviewComments: null,
      createdAt: now,
      updatedAt: now
    }

    // Create document
    const docRef = await addDoc(collection(db, 'timesheetEntries'), entryData)

    return {
      success: true,
      docId: docRef.id,
      data: {
        id: docRef.id,
        ...entryData
      }
    }
  } catch (error) {
    console.error('Create timesheet error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update existing timesheet entry (pending only)
 * @param {string} entryId - Entry document ID
 * @param {object} data - Updated entry data
 * @param {object} userData - User data
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const updateTimesheet = async (entryId, data, userData) => {
  try {
    // Check if entry exists and is pending
    const docRef = doc(db, 'timesheetEntries', entryId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return { success: false, error: 'Timesheet entry not found' }
    }

    const entry = docSnap.data()

    if (entry.status !== 'pending') {
      return { success: false, error: 'Only pending entries can be edited' }
    }

    // Validate updated entry
    const validation = await validateTimesheetEntry(data, userData.employeeId, entryId)

    if (!validation.valid) {
      return { success: false, error: validation.error }
    }

    // Get project name if changed
    let projectName = entry.projectName
    if (data.projectId !== entry.projectId) {
      const projectDoc = await getDoc(doc(db, 'projects', data.projectId))
      projectName = projectDoc.exists() ? projectDoc.data().name : 'Unknown Project'
    }

    const now = Timestamp.now()
    await updateDoc(docRef, {
      projectId: data.projectId,
      projectName: projectName,
      date: data.date,
      hours: parseFloat(data.hours),
      taskDescription: data.taskDescription.trim(),
      updatedAt: now
    })

    return { success: true }
  } catch (error) {
    console.error('Update timesheet error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Approve timesheet entry (admin only)
 * @param {string} entryId - Entry document ID
 * @param {object} adminData - Admin user data (uid, employeeId, role, fullName)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const approveTimesheet = async (entryId, adminData) => {
  try {
    // Check if entry exists and is pending
    const docRef = doc(db, 'timesheetEntries', entryId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return { success: false, error: 'Timesheet entry not found' }
    }

    const entry = docSnap.data()

    if (entry.status !== 'pending') {
      return { success: false, error: 'Only pending entries can be approved' }
    }


    // CRITICAL: Authorization check (EXACT pattern from timeOffService)
    // Super_admin can approve anything
    // Non-super_admin can only approve if:
    //   - Entry has reportsTo field AND
    //   - reportsTo matches adminData.employeeId
    if (adminData.role !== 'super_admin') {
      if (!entry.reportsTo || entry.reportsTo !== adminData.employeeId) {
        return {
          success: false,
          error: 'You can only approve timesheets from your direct reports'
        }
      }
    }

    const now = Timestamp.now()
    await updateDoc(docRef, {
      status: 'approved',
      reviewedBy: adminData.uid,
      reviewedByName: adminData.fullName || adminData.email,
      reviewedAt: now,
      updatedAt: now
    })

    return { success: true }
  } catch (error) {
    console.error('Approve timesheet error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Reject timesheet entry (admin only)
 * @param {string} entryId - Entry document ID
 * @param {object} adminData - Admin user data (uid, employeeId, role, fullName)
 * @param {string} comments - Rejection comments (required)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const rejectTimesheet = async (entryId, adminData, comments) => {
  try {
    // Validate comments
    if (!comments || !comments.trim()) {
      return { success: false, error: 'Rejection comments are required' }
    }

    if (comments.length > 500) {
      return { success: false, error: 'Comments exceed maximum length of 500 characters' }
    }

    // Check if entry exists and is pending
    const docRef = doc(db, 'timesheetEntries', entryId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return { success: false, error: 'Timesheet entry not found' }
    }

    const entry = docSnap.data()

    if (entry.status !== 'pending') {
      return { success: false, error: 'Only pending entries can be rejected' }
    }


    // CRITICAL: Authorization check (EXACT pattern from timeOffService)
    // Super_admin can reject anything
    // Non-super_admin can only reject if:
    //   - Entry has reportsTo field AND
    //   - reportsTo matches adminData.employeeId
    if (adminData.role !== 'super_admin') {
      if (!entry.reportsTo || entry.reportsTo !== adminData.employeeId) {
        return {
          success: false,
          error: 'You can only reject timesheets from your direct reports'
        }
      }
    }

    const now = Timestamp.now()
    await updateDoc(docRef, {
      status: 'rejected',
      reviewedBy: adminData.uid,
      reviewedByName: adminData.fullName || adminData.email,
      reviewedAt: now,
      reviewComments: comments.trim(),
      updatedAt: now
    })

    return { success: true }
  } catch (error) {
    console.error('Reject timesheet error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Cancel timesheet entry (employee only, pending entries only)
 * @param {string} entryId - Entry document ID
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const cancelTimesheet = async (entryId) => {
  try {
    // Check if entry exists and is pending
    const docRef = doc(db, 'timesheetEntries', entryId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return { success: false, error: 'Timesheet entry not found' }
    }

    const entry = docSnap.data()

    if (entry.status !== 'pending') {
      return { success: false, error: 'Only pending entries can be cancelled' }
    }

    const now = Timestamp.now()
    await updateDoc(docRef, {
      status: 'cancelled',
      updatedAt: now
    })

    return { success: true }
  } catch (error) {
    console.error('Cancel timesheet error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all employees (for admin filter dropdown)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllEmployees = async () => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('fullName', 'asc')
    )

    const querySnapshot = await getDocs(usersQuery)
    const employees = []

    querySnapshot.forEach((doc) => {
      const userData = doc.data()
      employees.push({
        employeeId: userData.employeeId,
        fullName: userData.fullName,
        email: userData.email
      })
    })

    return { success: true, data: employees }
  } catch (error) {
    console.error('Get all employees error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Helper: Format timestamp for display
 * @param {Timestamp} timestamp - Firebase Timestamp
 * @returns {string} - Formatted date
 */
export const formatTimestamp = (timestamp) => {
  try {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate()
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('Format timestamp error:', error)
    return 'Invalid date'
  }
}

/**
 * Helper: Format date for display
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {string} - Formatted date (e.g., "Oct 2, 2025")
 */
export const formatDate = (date) => {
  try {
    const dateObj = new Date(date + 'T00:00:00')
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  } catch (error) {
    console.error('Format date error:', error)
    return date
  }
}

/**
 * Helper: Format hours for display
 * @param {number} hours - Hours as decimal
 * @returns {string} - Formatted hours (e.g., "7.5h")
 */
export const formatHours = (hours) => {
  return `${hours.toFixed(1)}h`
}

/**
 * Helper: Get status badge variant
 * @param {string} status - Entry status
 * @returns {string} - Badge variant
 */
export const getStatusBadgeVariant = (status) => {
  const variants = {
    'pending': 'warning',
    'approved': 'success',
    'rejected': 'error',
    'cancelled': 'info'
  }
  return variants[status] || 'info'
}

/**
 * Helper: Get today's date in YYYY-MM-DD format
 * @returns {string} - Today's date
 */
export const getTodayDate = () => {
  const today = new Date()
  return today.toISOString().split('T')[0]
}

/**
 * Export timesheet entries to CSV file
 * @param {array} timesheetEntries - Array of timesheet entries to export
 * @returns {object} - {success: boolean, count?: number, error?: string}
 */
export const exportTimesheetsToCSV = (timesheetEntries) => {
  try {
    // Validate input
    if (!timesheetEntries || !Array.isArray(timesheetEntries)) {
      return { success: false, error: 'Invalid entries data' }
    }

    if (timesheetEntries.length === 0) {
      return { success: false, error: 'No entries to export' }
    }

    // Map entries to CSV format
    const csvData = timesheetEntries.map(entry => {
      // Format timestamps
      const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A'
        try {
          if (timestamp.toDate) {
            // Firebase Timestamp
            return timestamp.toDate().toISOString().split('T')[0]
          }
          // Regular date string
          return new Date(timestamp).toISOString().split('T')[0]
        } catch (error) {
          return 'N/A'
        }
      }

      // Escape and clean task description
      const cleanText = (text) => {
        if (!text) return 'N/A'
        // Remove extra whitespace and newlines
        return String(text).replace(/\s+/g, ' ').trim()
      }

      return {
        'Employee Name': entry.employeeName || 'N/A',
        'Employee ID': entry.employeeId || 'N/A',
        'Date': entry.date || 'N/A',
        'Project Name': entry.projectName || 'Unknown Project',
        'Hours': entry.hours || 0,
        'Task Description': cleanText(entry.taskDescription),
        'Status': entry.status || 'N/A',
        'Submitted At': formatTimestamp(entry.createdAt),
        'Reviewed By': entry.reviewedByName || 'N/A',
        'Reviewed At': formatTimestamp(entry.reviewedAt)
      }
    })

    // Convert to CSV using papaparse
    const csv = Papa.unparse(csvData, {
      quotes: true, // Quote all fields
      header: true
    })

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')

    if (link.download !== undefined) {
      // Create download link
      const url = URL.createObjectURL(blob)
      const today = getTodayDate()
      const filename = `timesheets_${today}.csv`

      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up URL
      setTimeout(() => URL.revokeObjectURL(url), 100)

      return { success: true, count: timesheetEntries.length }
    } else {
      return { success: false, error: 'Browser does not support file download' }
    }
  } catch (error) {
    console.error('Export to CSV error:', error)
    return { success: false, error: error.message || 'Failed to export CSV' }
  }
}

/**
 * Get pending timesheets count based on user role
 * @param {object} userData - Current user data
 * @returns {Promise<number>} - Count of pending timesheets
 */
export const getPendingTimesheetsCount = async (userData) => {
  try {
    let timesheetsQuery

    if (userData.role === 'super_admin') {
      // Count ALL pending timesheets
      timesheetsQuery = query(
        collection(db, 'timesheetEntries'),
        where('status', '==', 'pending')
      )
    } else if (userData.role === 'admin') {
      // Count pending from direct reports
      timesheetsQuery = query(
        collection(db, 'timesheetEntries'),
        where('status', '==', 'pending'),
        where('reportsTo', '==', userData.employeeId)
      )
    } else {
      // Count own pending timesheets
      timesheetsQuery = query(
        collection(db, 'timesheetEntries'),
        where('status', '==', 'pending'),
        where('employeeId', '==', userData.employeeId)
      )
    }

    const querySnapshot = await getDocs(timesheetsQuery)
    return querySnapshot.size
  } catch (error) {
    console.error('Get pending timesheets count error:', error)
    return 0
  }
}
